import type { NumberOfLines } from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import { ChecksIcon, FlagIcon, QuestionCircleIcon } from '~/components/icons';
import { DynamicClaimActionResultRequiresHumanApprovalReasonKey as WaitingReasonKey } from '~/server_shared/openapi/data-contracts';
import colors from '~/theme/tailwind/colors';

import type {
  ActionState,
  ActionStateAppearance,
  ActivityKind,
  AskState,
  KindAppearance,
  StateChipLook,
} from './types';

/** The log pane's opening share of the tab's width, in percent; the activity panel takes the rest. */
export const LOG_PANE_DEFAULT_SIZE = 68;

/**
 * How many of the claim's most recent events the log's live query holds, which is what the tab
 * opens with. Older entries come in on request: "Load earlier" reads back a page at a time, and
 * "Show in the log" reads in an older card's entry. The hub's own setting rather than the service
 * default, which also sizes chat, the Clive notifications feed and the FNOL timeline: those show
 * only their window, so a change made for them should not resize the log's.
 */
export const LOG_EVENT_LIMIT = 100;

/**
 * How much of an entry's message the log shows before Show more, and how much of a `ClampedText`
 * line a card shows in either pane. The conductor's can run long, and so can a handler's.
 */
export const LOG_MESSAGE_VISIBLE_LINES: NumberOfLines = 4;

/** An activity card's values, in the narrow column: a long one is clamped behind Show more. */
export const ACTIVITY_VALUE_VISIBLE_LINES: NumberOfLines = 2;

/**
 * How long before a card's `created_at` "Show in the log" starts reading for its entry, in minutes, and
 * before a stage change's `changed_at` a chapter of the stages rail does.
 * claims-ai saves the row before it publishes the entry, so the entry is never older than the row. The
 * slack covers clock drift between the database and Firestore, and Firestore comparing the stored
 * timestamp strings, which can differ from ours in precision and in how they write UTC.
 */
export const LOG_LOOKUP_SLACK_MINUTES = 5;

/** What "Show in the log", or a chapter of the stages rail, says when it cannot take the reader to the entry. */
export const LOG_LOOKUP_NOTICE = {
  notInLog: 'This card has no entry of its own in the log.',
  failed: "Couldn't load this card's log entry. Try again.",
  chapterNotInLog: 'Nothing the log shows comes after this stage change.',
  chapterFailed: "Couldn't load the log at this stage change. Try again.",
} as const;

/**
 * One icon per kind, and the same icon for every member of a kind.
 *
 * Giving each action type its own glyph was tried and rejected: it asks the reader to learn a
 * glyph per type to gain nothing the title does not already say in words, the set of types is
 * open so every new one needs an icon somebody invents, and it spends the one slot that says
 * *what kind of thing this is* on the thing's subject instead.
 *
 * A recommendation's mark is a flag — "this is on your list" — and never a tick. A tick means
 * *done*, and the state a recommendation is most often in when somebody looks at it is the one
 * where nothing has been done. Neither the mark nor its colours change with the state: what a thing
 * *is* does not change when it is executed, and the frame and the chip around it already say what
 * became of it.
 *
 * Both panels read this, which is the point — the same thing has to look the same in the log and
 * in the activity panel, and two hardcoded glyphs are two things to keep in step.
 */
export const KIND_APPEARANCE: Record<ActivityKind, KindAppearance> = {
  recommendation: { Icon: FlagIcon, label: 'Actions', chip: 'bg-blue-100', iconColor: colors.blue[600] },
  request: { Icon: ChecksIcon, label: 'Requests', chip: 'bg-purple-100', iconColor: colors.purple[600] },
  question: { Icon: QuestionCircleIcon, label: 'Questions', chip: 'bg-berry-400/30', iconColor: colors.berry[600] },
};

/**
 * Whether an action or an ask in this state is waiting on a person — the only definition there is.
 *
 * Three surfaces read it and they must agree: the activity panel's header count, its status
 * filter, and the waiting badge on the claim tab. A second definition is a defect, not an
 * optimisation, because the two can only ever disagree in front of a handler.
 *
 * A failed action counts. Nobody has to approve it, but it did not happen and somebody has to
 * know, which is the question the count answers. `unresolved` does not: nothing has established
 * that anything is waiting, and a count is a claim.
 *
 * A pending request or a pending question counts, and no other state an ask can be in: the handler
 * owes the work or the answer, and every other state is closed.
 *
 * A record rather than a predicate, so a new `ActionState` or `AskState` cannot compile until it
 * has answered this — the default a predicate would fall through to is exactly the wrong way to
 * decide it.
 */
export const ACTION_NEEDS_ATTENTION: Record<ActionState | AskState, boolean> = {
  pending: true,
  failed: true,
  executed: false,
  done: false,
  answered: false,
  rejected: false,
  withdrawn: false,
  dismissed: false,
  unresolved: false,
};

/**
 * The state alone, where there is no room for who did it.
 *
 * No "you" or "your": a claim file is read by the handler's colleagues, their team lead and an
 * auditor, and an entry that addresses one of them is wrong for the rest.
 */
export const ACTION_STATE_LABEL: Record<ActionState, string> = {
  pending: 'Awaiting approval',
  executed: 'Executed automatically',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn by Clive',
  dismissed: 'Dismissed',
  failed: "Couldn't be executed",
  unresolved: 'Status unknown',
};

/**
 * Decided, and no longer running: the three ways an action can close without being executed.
 *
 * They share one appearance because the reader's question is "does this still need me", which all three
 * answer identically — the chip says which of them it was. A request or a question that closed
 * without what Clive asked for wears it too, for the same reason.
 */
const SETTLED_APPEARANCE: ActionStateAppearance = {
  card: 'border-dashed border-grey-300',
  node: 'bg-grey-300',
};

/** The state chip of everything that closed without what Clive asked for, whatever its kind. */
const SETTLED_CHIP = 'bg-slate-200 text-slate-800';

/**
 * What an action looks like once it has a state — five appearances for seven states.
 *
 * `failed` is solid, in orange: it was attempted and refused, which is neither a decision nor over —
 * it is waiting on somebody to retry or reject it. `unresolved` is grey and solid-framed — grey
 * because it makes no claim, solid because the action is not closed, we simply have not read its
 * row yet.
 */
export const ACTION_STATE_APPEARANCE: Record<ActionState, ActionStateAppearance> = {
  pending: { card: 'border-blue-300', node: 'bg-blue-600' },
  executed: { card: 'border-dashed border-green-600', node: 'bg-green-600' },
  failed: { card: 'border-orange-700', node: 'bg-orange-700' },
  unresolved: { card: 'border-grey-300', node: 'bg-grey-300' },
  rejected: SETTLED_APPEARANCE,
  withdrawn: SETTLED_APPEARANCE,
  dismissed: SETTLED_APPEARANCE,
};

/** An action's state chip in each state, in both panes. */
export const ACTION_STATE_CHIP: Record<ActionState, string> = {
  pending: 'bg-blue-100 text-blue-700',
  executed: 'bg-green-100 text-green-700',
  failed: 'bg-orange-100 text-orange-700',
  unresolved: 'bg-slate-200 text-slate-800',
  rejected: SETTLED_CHIP,
  withdrawn: SETTLED_CHIP,
  dismissed: SETTLED_CHIP,
};

/**
 * A waiting action's chip, by why CMS held it for a person, in place of the `pending` one.
 *
 * Keyed by the causes CMS publishes, so a cause it adds cannot compile until it has a chip here.
 * Like `ACTION_STATE_LABEL`, no "you" or "your". The sentence under the chip is CMS's own.
 */
export const ACTION_WAITING_REASON_CHIP: Record<WaitingReasonKey, StateChipLook> = {
  [WaitingReasonKey.AlwaysHuman]: { label: 'Always needs approval', className: 'bg-blue-100 text-blue-700' },
  [WaitingReasonKey.BelowThreshold]: { label: 'Low confidence', className: 'bg-yellow-100 text-yellow-800' },
  [WaitingReasonKey.Guardrail]: { label: 'Flagged by a rule', className: 'bg-red-100 text-red-700' },
};

/**
 * A log entry about a version of an action that Clive has since revised. Settled, because the version
 * no longer stands and asks nothing of the reader; what became of the action is its last entry's to say.
 */
export const SUPERSEDED_ACTION_APPEARANCE: ActionStateAppearance = SETTLED_APPEARANCE;

/** The chip of such an entry, in place of the action's state. */
export const SUPERSEDED_ACTION_CHIP: StateChipLook = { label: 'Revised', className: SETTLED_CHIP };

/**
 * What a request looks like in each state.
 *
 * Purple while it waits on the handler: the kind's own colour, so the card says it is pending and
 * needs no chip to. Once closed its frame is dashed — still purple where the work arrived, grey
 * however else it closed — and the chip and the outcome line say how.
 */
export const REQUEST_STATE_APPEARANCE: Record<AskState, ActionStateAppearance> = {
  pending: { card: 'border-purple-600', node: 'bg-purple-600' },
  done: { card: 'border-dashed border-purple-600', node: 'bg-purple-600' },
  answered: SETTLED_APPEARANCE,
  rejected: SETTLED_APPEARANCE,
  withdrawn: SETTLED_APPEARANCE,
};

/**
 * A request's state chip in each state, in both panes. Purple while it waits on the handler, as its
 * card is. Green once its work arrived, as its frame is: the one way a request closes with what
 * Clive asked for. Every other closed request's chip wears the settled colours.
 */
export const REQUEST_STATE_CHIP: Record<AskState, string> = {
  pending: 'bg-purple-100 text-purple-700',
  done: 'bg-green-100 text-green-700',
  answered: SETTLED_CHIP,
  rejected: SETTLED_CHIP,
  withdrawn: SETTLED_CHIP,
};

/**
 * What a question looks like in each state.
 *
 * Pink while it waits on the handler's answer: the kind's own colour, as a waiting request's is
 * purple. Once closed its frame is dashed like a closed request's — still pink where it was
 * answered, grey however else it closed — and its record says how it closed.
 */
export const QUESTION_STATE_APPEARANCE: Record<AskState, ActionStateAppearance> = {
  pending: { card: 'border-berry-600', node: 'bg-berry-600' },
  done: SETTLED_APPEARANCE,
  answered: { card: 'border-dashed border-berry-600', node: 'bg-berry-600' },
  rejected: SETTLED_APPEARANCE,
  withdrawn: SETTLED_APPEARANCE,
};

/**
 * A question's state chip in each state. Pink while it waits on the handler's answer, as its card
 * is; green once answered, the one way a question closes with what Clive asked for. Every other
 * closed question's chip wears the settled colours.
 */
export const QUESTION_STATE_CHIP: Record<AskState, string> = {
  pending: 'bg-berry-500/20 text-berry-700',
  answered: 'bg-green-100 text-green-700',
  done: SETTLED_CHIP,
  rejected: SETTLED_CHIP,
  withdrawn: SETTLED_CHIP,
};
